import React, { useRef, useEffect, useState } from 'react';
import { Box, TextField, styled } from '@mui/material';

const OtpBox = styled(TextField)(({ theme, error }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        borderRadius: 12,
        transition: 'all 0.2s ease-in-out',
        '& fieldset': {
            borderColor: error ? theme.palette.error.main : theme.palette.divider,
            borderWidth: 2,
            borderRadius: 12,
        },
        '&:hover fieldset': {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-focused fieldset': {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            boxShadow: `0 0 0 4px ${error ? theme.palette.error.main : theme.palette.primary.main}20`,
        },
    },
    '& input': {
        textAlign: 'center',
        fontSize: '1.5rem',
        fontWeight: 600,
        padding: theme.spacing(1.5),
        width: '3rem',
        height: '3rem',
        color: theme.palette.text.primary,
    },
}));

/**
 * Six-digit OTP input component
 * @param {Object} props
 * @param {string} props.value - OTP value (string of digits)
 * @param {Function} props.onChange - Callback when value changes
 * @param {Function} props.onComplete - Callback when 6 digits are entered
 * @param {boolean} props.error - Error state
 * @param {boolean} props.disabled - Disabled state
 */
const OtpInput = ({ value = '', onChange, onComplete, error = false, disabled = false }) => {
    const [otp, setOtp] = useState(value.split(''));
    const inputRefs = useRef([]);

    // Update internal state when prop changes
    useEffect(() => {
        setOtp(value.split('').slice(0, 6));
    }, [value]);

    // Focus management
    useEffect(() => {
        if (inputRefs.current[0] && !disabled) {
            inputRefs.current[0].focus();
        }
    }, [disabled]);

    const handleChange = (index, newValue) => {
        // Only allow digits
        const digit = newValue.replace(/\D/g, '').slice(-1);

        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);

        // Call onChange with complete string
        const otpString = newOtp.join('');
        onChange?.(otpString);

        // Auto-focus next input if digit entered
        if (digit && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Call onComplete if all 6 digits entered
        if (otpString.length === 6 && onComplete) {
            onComplete(otpString);
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            e.preventDefault();

            if (otp[index]) {
                // Clear current box
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
                onChange?.(newOtp.join(''));
            } else if (index > 0) {
                // Move to previous box and clear it
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                onChange?.(newOtp.join(''));
                inputRefs.current[index - 1]?.focus();
            }
        }

        // Handle left/right arrow keys
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

        if (pastedData) {
            const newOtp = pastedData.split('');
            setOtp(newOtp);
            onChange?.(pastedData);

            // Focus last filled box or first empty box
            const focusIndex = Math.min(pastedData.length, 5);
            inputRefs.current[focusIndex]?.focus();

            // Call onComplete if 6 digits pasted
            if (pastedData.length === 6 && onComplete) {
                onComplete(pastedData);
            }
        }
    };

    const handleFocus = (index) => {
        // Select all text on focus for easy replacement
        inputRefs.current[index]?.select();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: { xs: 1, sm: 1.5 },
                justifyContent: 'center',
                direction: 'ltr' // Always LTR for OTP
            }}
        >
            {[0, 1, 2, 3, 4, 5].map((index) => (
                <OtpBox
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={otp[index] || ''}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onFocus={() => handleFocus(index)}
                    error={error}
                    disabled={disabled}
                    inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        autoComplete: 'off',
                    }}
                    sx={{
                        width: { xs: '2.5rem', sm: '3.5rem' },
                        '& input': {
                            width: { xs: '2.5rem', sm: '3.5rem' },
                            height: { xs: '2.5rem', sm: '3.5rem' },
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }
                    }}
                />
            ))}
        </Box>
    );
};

export default OtpInput;
